import { INDICES, QUERY_DEFAULTS } from './config';
import { QUERY_FIELD_NAMES } from './constants';

export type ElasticsearchQueryFilter = Record<string, unknown> & {
  query: {
    bool: {
      must?: Array<Record<string, unknown>>;
      should?: Array<Record<string, unknown>>;
      must_not?: Array<Record<string, unknown>>;
      minimum_should_match?: number;
    };
  };
};

export interface TermCondition {
  field: string;
  value: string | number | boolean;
}

export interface QueryParams {
  q?: string;
  index: string;
  from?: number;
  size?: number;
  deleted?: boolean;
  query_filter?: string;
  track_total_hits?: boolean | number;
  fetch_source?: boolean;
  sort_field?: string;
  sort_order?: 'asc' | 'desc';
  exclude_source_fields?: string;
}

export const buildQueryFilter = (termConditions: TermCondition[]): ElasticsearchQueryFilter => {
  if (termConditions.length === 0) {
    return { query: { bool: {} } };
  }

  const mustClauses = termConditions.map(({ field, value }) => ({
    term: { [field]: value },
  }));

  return {
    query: {
      bool: {
        must: mustClauses,
      },
    },
  };
};

export const buildQueryFilterFromClauses = (mustClauses: Array<Record<string, unknown>>): ElasticsearchQueryFilter => {
  if (mustClauses.length === 0) {
    return { query: { bool: {} } };
  }

  return {
    query: {
      bool: {
        must: mustClauses,
      },
    },
  };
};

export const createTermClause = (field: string, value: string | number | boolean): Record<string, unknown> => ({
  term: { [field]: value },
});

export const createShouldClause = (
  field: string,
  values: Array<string | number | boolean>
): Record<string, unknown> => ({
  bool: {
    should: values.map((value) => ({ term: { [field]: value } })),
    minimum_should_match: 1,
  },
});

/**
 * 서비스명 prefix 기반 Elasticsearch 쿼리 생성
 *
 * @description
 * 성숙도 필터링을 위해 서비스명의 prefix를 매칭하는 쿼리를 생성합니다.
 * 예: "gold-" prefix는 gold-iceberg, gold-postgres 등의 모든 서비스를 매칭합니다.
 *
 * @param prefix - 검색할 서비스명 prefix (예: "bronze-", "silver-", "gold-")
 * @returns Elasticsearch prefix 쿼리 객체
 *
 * @example
 * createServicePrefixClause("gold-")
 * // returns: { prefix: { "service.name.keyword": "gold-" } }
 */
export const createServicePrefixClause = (prefix: string): Record<string, unknown> => ({
  prefix: { [QUERY_FIELD_NAMES.SERVICE_NAME]: prefix },
});

/**
 * QuickFilter용 should 절 생성 (minimum_should_match 없음)
 * OpenMetadata의 트리 선택 필터는 minimum_should_match를 사용하지 않음
 */
export const createQuickFilterShouldClause = (
  field: string,
  values: Array<string | number | boolean>
): Record<string, unknown> => ({
  bool: {
    should: values.map((value) => ({ term: { [field]: value } })),
  },
});

export const buildTreeFilterClauses = (params: {
  serviceType?: string;
  serviceName?: string;
  databaseName?: string;
  schemaName?: string;
}): Array<Record<string, unknown>> => {
  const { serviceType, serviceName, databaseName, schemaName } = params;
  const clauses: Array<Record<string, unknown>> = [];

  if (serviceType) {
    clauses.push(createTermClause(QUERY_FIELD_NAMES.SERVICE_TYPE, serviceType));
  }

  if (serviceName) {
    clauses.push(createTermClause(QUERY_FIELD_NAMES.SERVICE_DISPLAY_NAME, serviceName));
  }

  if (databaseName) {
    clauses.push(createTermClause(QUERY_FIELD_NAMES.DATABASE_DISPLAY_NAME, databaseName));
  }

  if (schemaName) {
    clauses.push(createTermClause(QUERY_FIELD_NAMES.DATABASE_SCHEMA_DISPLAY_NAME, schemaName));
  }

  return clauses;
};

export const createQueryParams = (params: Partial<QueryParams>): QueryParams => {
  return {
    q: params.q ?? QUERY_DEFAULTS.Q,
    index: params.index ?? INDICES.DATA_ASSET,
    from: params.from ?? QUERY_DEFAULTS.FROM,
    size: params.size ?? QUERY_DEFAULTS.SIZE.STANDARD,
    deleted: params.deleted ?? QUERY_DEFAULTS.DELETED,
    ...params,
  };
};

export const createAggregationParams = (index: string, queryFilter?: ElasticsearchQueryFilter): QueryParams => {
  const params: QueryParams = {
    q: QUERY_DEFAULTS.Q,
    index,
    from: QUERY_DEFAULTS.FROM,
    size: QUERY_DEFAULTS.SIZE.AGGREGATION,
    deleted: QUERY_DEFAULTS.DELETED,
    track_total_hits: QUERY_DEFAULTS.TRACK_TOTAL_HITS,
    fetch_source: QUERY_DEFAULTS.FETCH_SOURCE,
  };

  if (queryFilter) {
    params.query_filter = JSON.stringify(queryFilter);
  }

  return params;
};

export const createSearchParams = (
  index: string,
  size: number,
  queryFilter?: ElasticsearchQueryFilter,
  excludeSourceFields?: string
): QueryParams => {
  const params: QueryParams = {
    q: QUERY_DEFAULTS.Q,
    index,
    from: QUERY_DEFAULTS.FROM,
    size,
    deleted: QUERY_DEFAULTS.DELETED,
  };

  if (queryFilter) {
    params.query_filter = JSON.stringify(queryFilter);
  }

  if (excludeSourceFields) {
    params.exclude_source_fields = excludeSourceFields;
  }

  return params;
};
